const prefix = (key: string) => `hightouch-${key}`;

export const clear = () => {
  try {
    // Entry point for local storage
    localStorage.clear();
  } catch (err) {
    console.warn("Failed to clear local storage", err);
  }
};

export const save = (key: string, value: any) => {
  try {
    // Entry point for local storage
    localStorage.setItem(prefix(key), JSON.stringify(value));
  } catch (err) {
    console.warn("Failed to save value to `%s` in local storage", key, err);
  }
};

// Counts unprefixed keys that start with `rawPrefix` without reading any values.
export const countRawKeysWithPrefix = (rawPrefix: string): number => {
  try {
    let count = 0;
    for (let i = 0; i < localStorage.length; i++) {
      if (localStorage.key(i)?.startsWith(rawPrefix)) {
        count++;
      }
    }
    return count;
  } catch (err) {
    console.warn("Failed to count `%s*` keys in local storage", rawPrefix, err);
    return 0;
  }
};

// Removes unprefixed keys that third-party SDKs write directly, except those in `keep`, and returns how many it removed.
export const removeRawKeysWithPrefix = (
  rawPrefix: string,
  keep: ReadonlySet<string>,
): number => {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key !== null && key.startsWith(rawPrefix) && !keep.has(key)) {
        doomed.push(key);
      }
    }
    for (const key of doomed) {
      localStorage.removeItem(key);
    }
    return doomed.length;
  } catch (err) {
    console.warn(
      "Failed to remove `%s*` keys from local storage",
      rawPrefix,
      err,
    );
    return 0;
  }
};

export const remove = (key: string) => {
  try {
    // Entry point for local storage
    localStorage.removeItem(prefix(key));
  } catch (err) {
    console.warn("Failed to remove `%s` from local storage", key, err);
  }
};

export const load = (key: string) => {
  try {
    // Entry point for local storage
    const value = localStorage.getItem(prefix(key));

    if (value) {
      return JSON.parse(value);
    }
  } catch (_err) {
    return null;
  }

  return null;
};

export type StorageEntry = { key: string; bytes: number };

/**
 * Iterate every entry currently in localStorage and return an array of
 * `{ key, bytes }`, where `bytes` is the UTF-16 byte cost of the key plus
 * the value (matching how the browser counts toward its quota).
 *
 * Callers should not read or forward the *values* — they may contain
 * customer data (e.g. unsaved SQL drafts). This helper exposes only the
 * key names and sizes so it is safe to use from monitoring/audit code.
 */
export const auditEntries = (): StorageEntry[] => {
  const entries: StorageEntry[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key === null) continue;
      const value = localStorage.getItem(key) ?? "";
      // localStorage strings are UTF-16; multiply length by 2 for byte count.
      const bytes = (key.length + value.length) * 2;
      entries.push({ key, bytes });
    }
  } catch (_err) {
    // Some browsers throw on iteration when storage is disabled; best-effort.
  }
  return entries;
};
